/**
 * TokenRevealBox — W2.4
 *
 * Zeigt einen Raw-API-Token einmalig an.
 * Wiederverwendbar fuer Create (W2.4) und zukünftige Nutzung (W2.5).
 * Light/Dark-Mode aware via CSS custom properties.
 * Horizontal-Scroll innerhalb Box — kein Text-Wrap.
 * Copy-to-Clipboard mit Bestätigungs-Feedback.
 */
import { useState } from 'react'
import { Copy, Check } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { copyToClipboard } from './tokenUtils'

type Props = {
  token: string
}

export function TokenRevealBox({ token }: Props) {
  const [copied, setCopied] = useState(false)
  const { t } = useTranslation('developer')

  async function handleCopy() {
    try {
      await copyToClipboard(token)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // Silent fail — Nutzer kann manuell kopieren
    }
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
      }}
    >
      {/* Warnhinweis */}
      <div
        role="alert"
        style={{
          padding: '10px 14px',
          background: 'color-mix(in srgb, var(--warning, #f59e0b) 12%, transparent)',
          border: '1px solid color-mix(in srgb, var(--warning, #f59e0b) 40%, transparent)',
          borderRadius: 8,
          fontSize: 13,
          color: 'var(--text-h)',
        }}
        data-testid="token-reveal-warning"
      >
        {t('tokenReveal.warning')}
      </div>

      {/* Token-Box + Copy-Button */}
      <div
        style={{
          display: 'flex',
          alignItems: 'stretch',
          gap: 8,
        }}
      >
        {/* Monospace Token-Box — horizontal scroll, kein wrap */}
        <div
          data-testid="token-reveal-box"
          style={{
            flex: 1,
            overflowX: 'auto',
            padding: '10px 14px',
            background: 'var(--surface, color-mix(in srgb, var(--text) 5%, transparent))',
            border: '1px solid var(--border)',
            borderRadius: 8,
            fontFamily: 'ui-monospace, Consolas, monospace',
            fontSize: 13,
            letterSpacing: '0.03em',
            color: 'var(--text-h)',
            whiteSpace: 'nowrap',
            wordBreak: 'keep-all',
            userSelect: 'all',
          }}
          aria-label={t('tokenReveal.tokenAriaLabel')}
        >
          {token}
        </div>

        {/* Copy-Button */}
        <button
          className="btn btn-icon btn-ghost"
          onClick={() => { void handleCopy() }}
          aria-label={copied ? t('tokenReveal.copiedLabel') : t('tokenReveal.copyLabel')}
          data-testid="token-copy-btn"
          style={{
            minWidth: 44,
            minHeight: 44,
            flexShrink: 0,
            color: copied ? 'var(--success, #16a34a)' : undefined,
            border: '1px solid var(--border)',
            borderRadius: 8,
          }}
        >
          {copied ? <Check size={16} aria-hidden="true" /> : <Copy size={16} aria-hidden="true" />}
        </button>
      </div>
    </div>
  )
}
